import { KeyboardEvent, useRef } from 'react';
import ReactDatePicker, { ReactDatePickerProps } from 'react-datepicker';
import { joinClassNames } from '../../../utils';
import { Label } from '../Label';
import { DatePickerFormat } from './enums';
import styles from '../styles.module.scss';

export type DatePickerProps = Omit<ReactDatePickerProps, 'id' | 'autoComplete' | 'dateFormat'> & {
  id: string;
  label: string;
  isRequired?: boolean;
  hideLabel?: boolean;
  autoComplete?: string;
  dateFormat?: DatePickerFormat;
  containerClassName?: string;
  tooltip?: string;
  // Tabbing doesn't behave well by default in react-datepicker and known issues exists:
  // https://github.com/Hacker0x01/react-datepicker/issues/3522
  closeOnTabKey?: boolean;
};

const extendDateFormats = (format: DatePickerFormat): string | string[] | undefined => {
  switch (format) {
    case DatePickerFormat.DateOnly:
      return ['dd-MM-yyyy', 'dd/MM/yyyy', 'dd MMM yyyy'];
    case DatePickerFormat.DateOnlyShortMonth:
      return [
        'dd MMM yyyy',
        'dd/MM/yy',
        'dd-MM-yy',
        'dd MMM yy',
        'dd MMMM yy',
        'dd MMMM yyyy',
        'dd-MM-yyyy',
        'dd/MM/yyyy',
      ];
    case DatePickerFormat.OmitDateShortMonth:
      return ['MMM yyyy'];
    default:
      return undefined;
  }
};

export const DatePicker = ({
  id,
  label,
  isRequired = false,
  hideLabel = false,
  dateFormat = DatePickerFormat.DateOnly,
  placeholderText = 'Select a date',
  autoComplete = 'off',
  containerClassName,
  tooltip,
  className,
  closeOnTabKey,
  ...props
}: DatePickerProps) => {
  const datePickerRef = useRef<ReactDatePicker>(null);

  const handleTab = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Tab') {
      if (datePickerRef?.current) {
        datePickerRef.current.setOpen(false);
      }
    }
  };

  return (
    <div className={containerClassName}>
      <Label htmlFor={id} isRequired={isRequired} hidden={hideLabel} tooltip={tooltip}>
        {label}
      </Label>
      <ReactDatePicker
        id={id}
        ref={datePickerRef}
        autoComplete={autoComplete}
        className={joinClassNames(styles.datePickerInput, !hideLabel && styles.hasLabel, className)}
        dateFormat={extendDateFormats(dateFormat)}
        placeholderText={placeholderText}
        onKeyDown={(event) => {
          if (closeOnTabKey) {
            handleTab(event);
          }
          props.onKeyDown?.(event);
        }}
        popperModifiers={[{ name: 'preventOverflow', enabled: true }]}
        {...props}
      />
    </div>
  );
};

DatePicker.displayName = 'DatePicker';
